import { navigateToApplication } from "./lib/utils";
import React, { useState, useEffect } from "react";
import Label from "./components/Label";
import { SliderWithInput } from "./components/SliderWithInput";
import { API } from "./API";
import {
  getLocalStorageVariables,
  setCssVariables,
  getInitialState,
} from "./lib/helpers";
import { IWidgetConfig, IQuestion, IQuote } from "./types";
import Select from "./components/Select";

const Loan: React.FC = () => {
  const [widget, setWidget] = useState<IWidgetConfig>({
    _id: "",
    headingText: "",
    questions: [],
    submissionButtonText: "",
    type: "",
    loanType: "",
    customCss: "",
  });
  const [quote, setQuote] = useState<IQuote>({
    amount: 0,
    externalPartnerId: "",
    source: "",
    sourceUrl: "",
    targetSystem: "",
    term: 0,
    type: "",
    usage: "",
    countryCode: "AU",
    loanPurpose: "",
  });

  const handleinitialConfig = async () => {
    const { loanType } = getLocalStorageVariables();
    const wConfig = await API.getWidgetConfig();
    const colors = wConfig.styles;

    setCssVariables(
      colors?.primary || "#5614BB",
      colors?.secondary || "#5614BB",
      colors?.tertiary || "#5614BB",
      colors?.quaternary || "#f2f2f2"
    );

    const w = wConfig.microProductJourney.find((w: any) => w.type === loanType);
    setWidget(w);

    let initialState = getInitialState(w);
    setQuote((prevQuote: IQuote) => ({
      ...prevQuote,
      ...initialState,
    }));

    // set custom css
    if (w.customCss) {
      const style = document.createElement("style");
      style.innerHTML = w.customCss;
      document.head.appendChild(style);
    }
  };

  useEffect(() => {
    handleinitialConfig();
  }, []);

  const handleSubmit = () => {
    localStorage.setItem("quote", JSON.stringify(quote));
    if (!quote.sourceUrl) return console.error("Source URL is missing");
    window.parent.location.href = navigateToApplication(quote.sourceUrl, "");
  };

  const handleInputChange = (name: string, value: number | string) => {
    let newState = { ...quote, [name]: value };
    setQuote(newState);
  };

  return (
    <div className="dw-p-2">
      <main className="dw-bg-white dw-shadow-md dw-drop-shadow-sm dw-p-3 md:dw-p-6 lg:dw-p-8 dw-rounded-2xl">
        <h1
          className="dw-text-primary dw-text-4xl md:dw-text-5xl dw-font-bold dw-py-2"
          id="Loan-Heading"
        >
          {widget?.headingText}
        </h1>
        <div
          className="dw-grid dw-grid-cols-1 md:dw-grid-cols-2 dw-gap-12 dw-items-end"
          id="LoanVariablesContainer"
        >
          {widget?.questions.map((question: IQuestion) => {
            switch (question.type) {
              case "slider":
                return (
                  <div key={question._id} id={`${question.fieldId}-container`}>
                    <Label text={question.label} />
                    <SliderWithInput
                      key={question._id}
                      capped={question.capped}
                      prefix={question.prefix}
                      id={question.fieldId}
                      suffix={question.suffix}
                      formatter={question.formatter}
                      defaultValue={[question.defaultValue] as number[]}
                      value={
                        [quote[question.mapping as keyof IQuote]] as number[]
                      }
                      max={question.max}
                      step={question.step}
                      min={question.min}
                      onValueChange={(value: number[]) =>
                        handleInputChange(question.mapping, value[0])
                      }
                    />
                  </div>
                );
              case "select":
                if (question.options.length == 0)
                  return <div className="dw-grid-placeholder"></div>;
                return (
                  <div key={question._id}>
                    {question.options.length > 0 && (
                      <div id={question.fieldId}>
                        <Label text={question.label} />
                        <Select
                          placeholder={question.defaultValue as string}
                          options={question.options}
                          value={
                            quote[question.mapping as keyof IQuote] as string
                          }
                          onChange={(value: string) =>
                            handleInputChange(question.mapping, value)
                          }
                        />
                      </div>
                    )}
                  </div>
                );
            }
          })}
          <div
            className="dw-flex dw-items-center dw-justify-end md:dw-col-start-2"
            id="widget-submission-div"
          >
            <p
              onClick={handleSubmit}
              id="widget-submission-button"
              className="dw-cursor-pointer dw-flex dw-items-center dw-bg-primary dw-min-w-fit dw-px-6 dw-py-2 dw-justify-center dw-rounded-full dw-text-white dw-font-bold dw-w-48 dw-h-10"
            >
              {widget?.submissionButtonText}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Loan;
